import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import {
  AppBar,
  Toolbar,
  Button,
  Avatar,
  Box,
  IconButton,
  styled,
  Badge,
  InputBase,
  alpha,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Alert,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  VerifiedUser as TeamsIcon,
  WarningAmber as WarningIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import type { AppHeaderProps } from './types';
import { ROUTES } from '../../routes/routes';

// Styled components for the header
const SearchWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  maxWidth: '300px',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    height: '24px',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const HeaderIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  color: 'text.secondary', // Changed to use theme color for better visibility
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
}));

export const AppHeader: React.FC<AppHeaderProps> = ({
  actions = [],
  onAuthLogout,
  onTeamUserLogout,
  drawerWidth = 240,
  collapsed = false,
  onToggleSidebar,
  onboardingBanner,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const actualDrawerWidth = collapsed ? 64 : drawerWidth;

  // Effect to detect fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle profile menu open
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle profile menu close
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle menu actions
  const handleMenuAction = (action: string) => {
    handleProfileMenuClose();

    switch (action) {
      case 'profile':
        // Navigate to profile page - check if we're in superadmin context
        if (window.location.pathname.startsWith('/superadmin')) {
          navigate('/superadmin/profile');
        } else {
          navigate(ROUTES.PROFILE);
        }
        break;
      case 'merchant-teams':
        // Navigate to change password page
        navigate(ROUTES.MERCHANT_TEAMS);
        break;
      case 'password':
        // Navigate to change password page
        navigate(ROUTES.CHANGE_PASSWORD);
        break;
      case 'settings':
        // Navigate to settings page
        navigate(ROUTES.SETTINGS);
        break;
      case 'authLogout':
        if (onAuthLogout) onAuthLogout();
        break;
      case 'teamUserLogout':
        if (onTeamUserLogout) onTeamUserLogout();
        break;
      default:
        break;
    }
  };

  const userInfo = user;


  return (
    <>


      {/* Main AppBar with top margin to account for alert */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', sm: `calc(100% - ${actualDrawerWidth}px)` },
          ml: { xs: 0, sm: `${actualDrawerWidth}px` },
          mt: '0px',
          backgroundColor: 'rgb(252, 252, 255)',
          color: 'text.primary',
          zIndex: theme => theme.zIndex.drawer,
          transition: theme =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Toolbar sx={{
          position: 'relative',
          justifyContent: 'space-between',
          minHeight: { xs: '40px', sm: '56px' },
          px: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          gap: 1,
        }}>
          {/* Left Section: Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {/* Toggle Button */}
            {onToggleSidebar && (
              <IconButton
                onClick={onToggleSidebar}
                sx={{
                  mr: { xs: 1, sm: 2 },
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgb(252, 252, 255)',
                  },
                }}
              >
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            )}
          </Box>

          {/* Center: Onboarding banner when KYC incomplete */}
          {onboardingBanner && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)',
                border: '1.5px solid',
                borderColor: '#FFB84D',
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)',
                maxWidth: '600px',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                }}
              >
                <WarningIcon sx={{ color: 'white', fontSize: 18 }} />
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#663C00', 
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  flex: 1,
                }}
              >
                {onboardingBanner.message}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={onboardingBanner.onActivate}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2.5,
                    py: 0.75,
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FB8C00 0%, #EF6C00 100%)',
                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
                    },
                  }}
                >
                  Activate Now
                </Button>
                <IconButton
                  size="small"
                  onClick={onboardingBanner.onRemindLater}
                  sx={{
                    color: '#663C00',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Right Section: Icons and User Profile */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 }
          }}>
            {/* Custom Actions Section */}
            {actions.length > 0 && (
              <Box sx={{
                display: { xs: 'none', sm: 'flex' },
                gap: 1,
                mr: { xs: 1, sm: 2 }
              }}>
                {actions.map(action => (
                  <Button
                    key={action.id}
                    variant={action.variant || 'text'}
                    color={action.color || 'primary'}
                    onClick={action.onClick}
                    startIcon={action.icon}
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 2 }
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* User Section with avatar */}
            {user && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: { xs: 1, sm: 2 },
                  pl: { xs: 1, sm: 2 },
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  sx={{
                    p: { xs: 0.5, sm: 1 }
                  }}
                >
                  <Avatar
                    src={user.avatar || undefined}
                    alt={user.name}
                    sx={{
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      backgroundColor: '#F6F7FA',
                      border: '2px solid',
                      borderColor: 'background.paper',
                      color: '#17A6DA',
                    }}
                  >
                    {!user?.avatar ? (
                      (user?.name || user?.user_name || 'U').charAt(0).toUpperCase()
                    ) : null}
                  </Avatar>
                </IconButton>

                {/* Enhanced Profile Menu */}
                <Menu
                  id="account-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleProfileMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'account-button',
                    sx: {
                      padding: '0 !important', // Target the .css-1toxriw-MuiList-root-MuiMenu-list class
                    },
                  }}
                  PaperProps={{
                    elevation: 8,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 4px 15px rgba(0,0,0,0.15))',
                      mt: 1.5,
                      minWidth: 280,
                      borderRadius: '12px',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 16,
                        width: 12,
                        height: 12,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                      // Target the MUI Menu list specifically
                      '& .MuiList-root.MuiMenu-list': {
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {/* User header section */}
                  <Box
                    sx={{
                      px: 3,
                      py: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'rgb(252, 252, 255)',
                      color: 'rgb(40, 42, 48)',
                      borderRadius: '12px 12px 0 0',
                    }}
                  >
                    <Avatar
                      src={user?.avatar}
                      alt={user?.name || ''}
                      sx={{
                        width: 48,
                        height: 48,
                        border: '2px solid rgb(236, 239, 242)',
                        color: '#17A6DA',
                        bgcolor: 'rgb(236, 239, 242)',
                      }}
                    >
                      {!user?.avatar ? (
                        (user?.name || user?.user_name || 'U').charAt(0).toUpperCase()
                      ) : null}
                    </Avatar>
                    <Box>
                      <Box
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {userInfo?.name || user?.name || user?.user_name || 'Guest'}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.75rem',
                          opacity: 0.85,
                        }}
                      >
                        {userInfo?.email || user?.email || user?.user_email || 'user@example.com'}
                      </Box>
                    </Box>
                  </Box>

                  {/* Menu items with enhanced styling */}
                  <Box sx={{ pt: 2, pb: 1 }}>
                    <MenuItem
                      onClick={() => {
                        handleMenuAction('profile');
                      }}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgb(227, 238, 255)',
                          transform: 'translateY(-2px)',
                          '& .MuiListItemIcon-root': {
                            color: 'rgb(40, 42, 48)',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'rgb(40, 42, 48)',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'primary.main',
                          minWidth: '40px',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          mr: 2,
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Profile View"
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      />
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleMenuAction('merchant-teams')}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgb(227, 238, 255)',
                          transform: 'translateY(-2px)',
                          '& .MuiListItemIcon-root': {
                            color: 'rgb(40, 42, 48)',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'rgb(40, 42, 48)',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'primary.main',
                          minWidth: '40px',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          mr: 2,
                        }}
                      >
                        <TeamsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Team and Role"
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      />
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleMenuAction('password')}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgb(227, 238, 255)',
                          transform: 'translateY(-2px)',
                          '& .MuiListItemIcon-root': {
                            color: 'rgb(40, 42, 48)',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'rgb(40, 42, 48)',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'primary.main',
                          minWidth: '40px',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          mr: 2,
                        }}
                      >
                        <VpnKeyIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Change Password"
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      />
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleMenuAction('settings')}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgb(227, 238, 255)',
                          transform: 'translateY(-2px)',
                          '& .MuiListItemIcon-root': {
                            color: 'rgb(40, 42, 48)',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'rgb(40, 42, 48)',
                          },
                        },
                      }}                    >
                      <ListItemIcon
                        sx={{
                          minWidth: '40px',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          mr: 2,
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Settings"
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                        }}
                      />
                    </MenuItem>
                  </Box>

                  <Divider sx={{ mx: 2, my: 1 }} />
                  {/* Show logout button for any authenticated user */}
                  {user && (
                    <Box sx={{ px: 0, pb: 2, pt: 1 }}>
                      <MenuItem
                        onClick={() => {
                          // Use appropriate logout action based on user type
                          if (user?.isTeamUser === true) {
                            handleMenuAction('teamUserLogout');
                          } else {
                            handleMenuAction('authLogout');
                          }
                        }}
                        sx={{
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgb(227, 238, 255)',
                            transform: 'translateY(-2px)',
                            '& .MuiListItemIcon-root': {
                              color: 'rgb(40, 42, 48)',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'rgb(40, 42, 48)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: '40px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            mr: 2,
                          }}
                        >
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Logout"
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                          }}
                        />
                      </MenuItem>
                    </Box>
                  )}

                </Menu>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};
